/**
 * Country access tier — second axis of the carrier-eligibility composition.
 *
 * Splits "what's the product status of this country" (this file) from
 * "what's Shippo's technical capability for this country"
 * (`lib/shipping/origin-support.ts::SHIPPO_ORIGIN_SUPPORT`).
 *
 * The full eligibility predicate composes THREE axes:
 *   1. capability — does Shippo rate from this origin? (probe-verified;
 *      lives in `origin-support.ts::SHIPPO_ORIGIN_SUPPORT`)
 *   2. access — is this country GA, beta, or off-limits for non-admin? (THIS FILE)
 *   3. currency-match — does the listing's currency match the origin's
 *      native currency? (Buffon-class guard; lives in `carrier-eligibility.ts`)
 *
 * Why a separate axis: probe-verified capability (US/GB/DE/FR) ≠ MVP scope
 * (US-only GA). The old `acceptedOrigins: ["US"]` flattening conflated the
 * two and produced UI-vs-server drift (UI showed "live-rate available with
 * limited disclosure" for FR; server gate rejected the save). Splitting
 * means: adding a country to GA = bump `COUNTRY_ACCESS[XX]` `beta → ga`,
 * one file changed, zero seller migration.
 *
 * Per-seller beta gate (the actual "is THIS seller in the live-rate beta")
 * uses the existing `beta_features` infrastructure — see
 * `lib/beta-features.ts::SHIPPO_LIVE_RATE_BETA_KEY` + `hasBetaFeature`.
 * That helper is async (RPC call), so the composed eligibility check is
 * async too — see `carrier-eligibility.ts::isCarrierAvailableForSeller`.
 *
 * Anchor question (type-driven-design §7): adding a country should change
 * exactly ONE entry here. Switches on `CountryAccess` downstream must end
 * with `default: unreachable(x: never)`.
 *
 * @see lib/shipping/origin-support.ts — capability axis
 * @see lib/shipping/carrier-eligibility.ts — composed resolver
 * @see lib/beta-features.ts — per-seller beta gate
 * @see .tondocs/shippo/CARRIER_ELIGIBILITY_HANDOFF_2026_06_25.md — design rationale
 */

/**
 * Access tier for a country in the live-rate carrier rollout.
 *
 * - `ga` — generally available; any seller from this country gets live-rate
 *   (subject to capability + currency-match).
 * - `beta` — gated by per-seller `beta_features` flag
 *   (`SHIPPO_LIVE_RATE_BETA_KEY`). Non-enrolled sellers get treated as `off`.
 * - `off` — capability may exist (probed and works) but product hasn't
 *   opened the country to anyone yet. Equivalent to "unsupported" from the
 *   server's point of view.
 */
export type CountryAccess = "ga" | "beta" | "off";

/**
 * Per-country access tier. Keys are uppercase ISO-3166-1 alpha-2.
 *
 * Lookup via {@link getCountryAccess} (case-insensitive, defaults to `off`
 * for any country not in the map).
 *
 * **Current scope (US GA'd 2026-08-07):** US is `ga` — every US seller sees
 * the live-rate UI and passes the server gate with no `beta_features` row.
 * GB/DE/FR stay `beta`: each still needs an explicit
 * `beta_features (user_id, 'shippo_live_rate_beta')` row. Everything else is
 * `off`.
 *
 * A leftover beta row on a US seller is harmless — `ga` ignores the flag
 * entirely (see the decision table on {@link getEffectiveTier}), so enrolment
 * rows never needed cleaning up at the flip.
 *
 * The entries match the probe-verified capability map in
 * `origin-support.ts::SHIPPO_ORIGIN_SUPPORT` — Shippo CAN rate from all four;
 * `beta` is a product decision about who may see it, not a capability claim.
 *
 * Dev/staging shortcut: set `BETA_FEATURES_BYPASS=shippo_live_rate_beta`
 * in `.env.local` to bypass enrolment for every seller without touching
 * the table. The bypass is fail-closed on `NODE_ENV=production`
 * (`lib/beta-features.ts::isBetaFeatureBypassed`).
 *
 * To open a country to GA: change its entry here from `beta` to `ga`. To
 * open a NEW country: add capability row in `origin-support.ts` FIRST
 * (probe-verified), then add an entry here (`off`/`beta`/`ga`).
 */
export const COUNTRY_ACCESS: Readonly<Record<string, CountryAccess>> = {
  US: "ga",
  GB: "beta",
  DE: "beta",
  FR: "beta",
  // Anything else → `off` via the fallback in getCountryAccess.
};

/**
 * Look up access tier for a country code (case-insensitive). Returns `off`
 * for any country not in {@link COUNTRY_ACCESS}, including null/undefined
 * inputs — callers should treat `off` as "reject".
 */
export function getCountryAccess(
  country: string | null | undefined,
): CountryAccess {
  if (!country) return "off";
  return COUNTRY_ACCESS[country.toUpperCase()] ?? "off";
}

// ---------------------------------------------------------------------------
// Effective UI tier — composes capability + access for container components
// ---------------------------------------------------------------------------

import { getOriginSupport, type SupportTier } from "./origin-support";

/**
 * Compose capability + access into the single `SupportTier | null` value
 * the `ShippingModeToggleAndParcelForm` Primitive consumes via its
 * `originTier` prop.
 *
 * Decision table:
 *
 *   | capability  | access | hasBetaAccess | effective tier         |
 *   |-------------|--------|---------------|------------------------|
 *   | null        | any    | any           | `null` (no address)    |
 *   | any         | off    | any           | "unsupported"          |
 *   | any         | beta   | false         | "unsupported"          |
 *   | broad       | ga     | any           | "broad"                |
 *   | limited     | ga     | any           | "limited"              |
 *   | broad       | beta   | true          | "broad"                |
 *   | limited     | beta   | true          | "limited"              |
 *
 * Why a helper: the UI tier and the server eligibility predicate need to
 * agree on the same matrix. Containers used to call `getOriginSupport(...).tier`
 * directly (capability-only) which is what produced the original bug —
 * UI promised live-rate for beta-tier countries the server then rejected.
 *
 * `country` null/undefined → `null` (the Primitive renders an "address-setup"
 * prompt rather than "unsupported"). All other rejections collapse to
 * `"unsupported"` so the Primitive disables the toggle uniformly.
 *
 * `hasBetaAccess` defaults to `false` (strict / fail-closed) — surfaces
 * that haven't yet wired the per-seller flag get the conservative answer.
 *
 * @see lib/shipping/carrier-eligibility.ts — server moment-of-truth
 * @see components/shipping/ShippingModeToggleAndParcelForm.tsx — `originTier`
 */
export function getEffectiveTier(
  country: string | null | undefined,
  hasBetaAccess = false,
): SupportTier | null {
  if (!country) return null;
  const cap = getOriginSupport(country);
  if (!cap) return "unsupported";
  const access = getCountryAccess(country);
  if (access === "off") return "unsupported";
  if (access === "beta" && !hasBetaAccess) return "unsupported";
  return cap.tier;
}
